import type { FC, FormEvent } from "react";
import { useState } from "react";

import Spinner from "../components/Spinner";

export interface CivilianLookupValues {
  firstName: string;
  lastName: string;
  checkInDate: string;
  checkOutDate: string | null;
}

interface CivilianLookupStepProps {
  onSubmit: (values: CivilianLookupValues) => void;
  onBack: () => void;
  onRetry?: () => void;
  isSubmitting: boolean;
  error: string;
  status?: "entry" | "not-found";
  flow?: "checkin" | "checkout" | "request" | "reservation";
}

const CivilianLookupStep: FC<CivilianLookupStepProps> = ({
  onSubmit,
  onBack,
  onRetry,
  isSubmitting,
  error,
  status = "entry",
  flow = "checkin",
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [checkInDate, setCheckInDate] = useState("");
  const [checkOutDate, setCheckOutDate] = useState("");
  const [hasNoCheckOutDate, setHasNoCheckOutDate] = useState(false);

  const canContinue =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    checkInDate.length > 0 &&
    (checkOutDate.length > 0 || hasNoCheckOutDate) &&
    !isSubmitting;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!canContinue) return;
    onSubmit({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      checkInDate,
      checkOutDate: hasNoCheckOutDate ? null : checkOutDate,
    });
  };

  if (status === "not-found") {
    return (
      <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-6">
        <div className="bc-fade-up w-full max-w-sm space-y-6 text-center">
          <h1 className="font-oswald text-4xl font-medium uppercase tracking-wide text-white">
            See Staff
          </h1>
          <p className="text-base font-medium leading-7 text-white/65">
            We could not safely find exactly one reservation. Check spelling and
            arrival date, or see staff to{" "}
            {flow === "checkout"
              ? "check out"
              : flow === "request"
                ? "continue"
                : flow === "reservation"
                  ? "continue"
                  : "check in"}
            .
          </p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="w-full rounded-xl bg-accentGreen px-8 py-4 font-oswald text-base font-medium uppercase tracking-wider text-primaryDarkBlue shadow-lg shadow-accentGreen/20 transition-all active:scale-[0.98]"
            >
              Try Again
            </button>
          )}
          <button
            type="button"
            onClick={onBack}
            className="w-full py-3 text-sm font-medium text-white/35 transition-colors hover:text-white/50"
          >
            Start Over
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative z-10 flex flex-1 flex-col items-center px-6">
      <form
        onSubmit={handleSubmit}
        className="grid w-full max-w-sm flex-1 grid-rows-[1fr_auto_1fr]"
      >
        <div className="flex flex-col justify-end pb-8">
          <div className="bc-fade-up text-center">
            <img
              src={
                new URL("../assets/ths-footer-logo.svg", import.meta.url).href
              }
              alt="Team Housing Solutions"
              className="mx-auto w-48 sm:w-56"
            />
          </div>
          <div className="bc-fade-up bc-fade-up-1 mt-10 h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          <div className="bc-fade-up bc-fade-up-1 mt-10 text-center">
            <h1 className="font-oswald text-4xl font-medium uppercase tracking-wide text-white sm:text-5xl">
              {flow === "checkout"
                ? "Civilian Check Out"
                : flow === "request"
                  ? "Report an Issue"
                  : flow === "reservation"
                    ? "Find Your Stay"
                    : "Civilian Check In"}
            </h1>
            <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/25">
              Basecamp Lodging
            </p>
          </div>
        </div>

        <div className="bc-fade-up bc-fade-up-2 space-y-3">
          <div>
            <label
              htmlFor="civilian-first-name"
              className="mb-2 block text-center text-[10px] font-bold uppercase tracking-[0.2em] text-white/30"
            >
              First Name
            </label>
            <input
              id="civilian-first-name"
              type="text"
              autoComplete="given-name"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              className="w-full rounded-xl border border-white/[0.08] bg-white/[0.03] p-4 text-center text-base font-medium text-white outline-none transition-all placeholder:text-white/15 focus:border-accentGreen/40 focus:bg-white/[0.05] focus:ring-0"
            />
          </div>
          <div>
            <label
              htmlFor="civilian-last-name"
              className="mb-2 block text-center text-[10px] font-bold uppercase tracking-[0.2em] text-white/30"
            >
              Last Name
            </label>
            <input
              id="civilian-last-name"
              type="text"
              autoComplete="family-name"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              className="w-full rounded-xl border border-white/[0.08] bg-white/[0.03] p-4 text-center text-base font-medium text-white outline-none transition-all placeholder:text-white/15 focus:border-accentGreen/40 focus:bg-white/[0.05] focus:ring-0"
            />
          </div>
          <div>
            <label
              htmlFor="civilian-arrival-date"
              className="mb-2 block text-center text-[10px] font-bold uppercase tracking-[0.2em] text-white/30"
            >
              Arrival Date
            </label>
            <input
              id="civilian-arrival-date"
              type="date"
              value={checkInDate}
              onChange={(event) => setCheckInDate(event.target.value)}
              className="w-full rounded-xl border border-white/[0.08] bg-white/[0.03] p-4 text-center text-base font-medium text-white outline-none transition-all focus:border-accentGreen/40 focus:bg-white/[0.05] focus:ring-0"
            />
          </div>
          <div>
            <label
              htmlFor="civilian-departure-date"
              className="mb-2 block text-center text-[10px] font-bold uppercase tracking-[0.2em] text-white/30"
            >
              Departure Date
            </label>
            <input
              id="civilian-departure-date"
              type="date"
              value={checkOutDate}
              disabled={hasNoCheckOutDate}
              onChange={(event) => {
                setCheckOutDate(event.target.value);
                if (event.target.value) setHasNoCheckOutDate(false);
              }}
              className="w-full rounded-xl border border-white/[0.08] bg-white/[0.03] p-4 text-center text-base font-medium text-white outline-none transition-all focus:border-accentGreen/40 focus:bg-white/[0.05] focus:ring-0 disabled:opacity-35"
            />
          </div>
          <label className="flex items-center gap-3 rounded-xl border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-sm font-medium text-white/55">
            <input
              type="checkbox"
              checked={hasNoCheckOutDate}
              onChange={(event) => {
                const isChecked = event.target.checked;
                setHasNoCheckOutDate(isChecked);
                if (isChecked) setCheckOutDate("");
              }}
              className="size-4 rounded border-white/20 bg-white/10 text-accentGreen focus:ring-accentGreen/40"
            />
            I do not have a departure date yet
          </label>
        </div>

        <div className="bc-fade-up bc-fade-up-3 pt-5">
          {error && (
            <div className="mb-5 rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-3 text-center text-sm font-medium text-red-300">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={!canContinue}
            className={`w-full rounded-xl px-8 py-4 font-oswald text-base font-medium uppercase tracking-wider transition-all active:scale-[0.98] ${
              canContinue
                ? "bg-accentGreen text-primaryDarkBlue shadow-lg shadow-accentGreen/25"
                : "bg-accentGreen/20 text-accentGreen/40"
            }`}
          >
            {isSubmitting ? (
              <span className="inline-flex items-center gap-2">
                <Spinner />
                Looking up...
              </span>
            ) : (
              "Continue"
            )}
          </button>
          <button
            type="button"
            onClick={onBack}
            className="mt-3 w-full py-3 text-sm font-medium text-white/25 transition-colors hover:text-white/40"
          >
            Go Back
          </button>
        </div>
      </form>
    </div>
  );
};

export default CivilianLookupStep;
